import React from 'react';
import { EMOJI_SET } from './redbook-emoji';

// 使用 import.meta.glob 批量导入所有表情图片（Vite 兼容方式）
const emojiImages = import.meta.glob('./redbook-emoji/*.png', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;

/**
 * 获取小红书表情图片路径
 * @param emojiText 表情文本，如 [微笑R]
 * @returns 图片路径或 null
 */
export const getRedbookEmojiImage = (emojiText: string): string | null => {
  if (!EMOJI_SET.has(emojiText)) {
    return null;
  }
  const imageName = emojiText.replace(/[\[\]]/g, '');
  const key = `./redbook-emoji/${imageName}.png`;
  return emojiImages[key] || null;
};

/**
 * 将文本中的小红书表情转换为 HTML 字符串
 * @param text 包含表情标识的文本
 * @returns HTML 字符串
 */
export const parseRedbookEmoji = (text: string): string => {
  if (!text) return '';

  return text.replace(/\[([^\]]+)\]/g, match => {
    const imageSrc = getRedbookEmojiImage(match);
    if (imageSrc) {
      return `<img src="${imageSrc}" alt="${match}" class="redbook-emoji-inline" />`;
    }
    return match;
  });
};

/**
 * 将文本中的小红书表情转换为 React 元素数组
 * @param text 包含表情标识的文本
 * @returns React 节点数组
 */
export const parseRedbookEmojiToReact = (text: string): React.ReactNode[] => {
  if (!text) return [];

  const regex = /\[([^\]]+)\]/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // 添加表情前的文本
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }

    // 尝试渲染表情
    const imageSrc = getRedbookEmojiImage(match[0]);
    if (imageSrc) {
      result.push(
        React.createElement('img', {
          key: `emoji-${match.index}`,
          src: imageSrc,
          alt: match[0],
          className: 'redbook-emoji-inline',
          style: { width: '20px', height: '20px', verticalAlign: 'middle', margin: '0 2px' }
        })
      );
    } else {
      result.push(match[0]);
    }

    lastIndex = regex.lastIndex;
  }

  // 添加剩余文本
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result.length > 0 ? result : [text];
};

/**
 * 检查文本是否包含小红书表情
 * @param text 要检查的文本
 * @returns 是否包含小红书表情
 */
export const hasRedbookEmoji = (text: string): boolean => {
  if (!text) return false;
  const regex = /\[([^\]]+)\]/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (EMOJI_SET.has(match[0])) {
      return true;
    }
  }
  return false;
};
